"use client";

import { Price, Tag, Text, type TextProps } from "@pamperedchef/design-system";
import * as styles from "./PriceDisplay.module.scss";

/**
 * DS `Price` typography token per layout size for the bold current-price row —
 * mirrors the internal `LABEL_SIZES` map so the locally-rendered "Free"/"$0.00"
 * value matches a non-zero price rendered by `Price`.
 */
const CURRENT_PRICE_STYLED_AS: Record<
	NonNullable<PriceDisplayProps["size"]>,
	TextProps["styledAs"]
> = {
	small: "sml",
	large: "price-1",
	compact: "sml"
};

/** DS `Price` only ships small/large; `compact` reuses small + a CSS size. */
const DS_PRICE_SIZE: Record<
	NonNullable<PriceDisplayProps["size"]>,
	"small" | "large"
> = {
	small: "small",
	large: "large",
	compact: "small"
};

function formatPrice(value: number, locale: string, currency: string): string {
	return new Intl.NumberFormat(locale, {
		style: "currency",
		currency
	}).format(value);
}

export interface PriceDisplayProps {
	/** Effective price shown in the bold row. `0` renders as "Free". */
	display?: number;
	/** Optional struck-through original price. */
	strikeThrough?: number;
	currency?: string;
	locale?: string;
	/** Renders a purple "Half-Price Combo" pill next to the price. */
	halfPriceCombo?: boolean;
	/** Renders a green "Kit Credit" pill next to the price. */
	kitCredit?: boolean;
	/** Renders a "Consultant pricing" sub-label under the price. */
	consultantOnly?: boolean;
	/** Layout size — small inline, large for hero, compact for dense bars. */
	size?: "small" | "large" | "compact";
	className?: string;
}

/**
 * Product price row composing the DS `Price` primitive with PC pricing
 * concepts: half-price combo, kit credit, and consultant-only indicator.
 * Used inside `ProductSummary`, `OfferCard`, `KitCard`, and `KitDetailsModal`
 * (STORE-007 — drop-in for the prior `PriceDisplay` stub).
 */
export function PriceDisplay({
	display,
	strikeThrough,
	currency = "USD",
	locale = "en-US",
	halfPriceCombo,
	kitCredit,
	consultantOnly,
	size = "small",
	className
}: PriceDisplayProps) {
	// DS `Price` renders its display value via `{display && …}`, so `display={0}`
	// falls through as a literal "0" rather than a formatted "$0.00". Render the
	// zero case here — ahead of `Price` so the current price leads the struck
	// original — and pass `undefined` to `Price` so it emits only the strikethrough.
	const isZeroDisplay = display === 0;

	return (
		<div
			className={[styles["price"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="PriceDisplay"
			data-size={size}
		>
			{isZeroDisplay ? (
				<Text
					tag="span"
					styledAs={CURRENT_PRICE_STYLED_AS[size]}
					className={styles["price__current"]}
				>
					{formatPrice(0, locale, currency)}
				</Text>
			) : null}
			<Price
				display={isZeroDisplay ? undefined : display}
				strikeThrough={strikeThrough}
				locale={locale}
				currency={currency}
				size={DS_PRICE_SIZE[size]}
			/>
			{halfPriceCombo ? (
				<Tag
					label="Half-Price Combo"
					kind="solid"
					size="sm"
					color="purple"
				/>
			) : null}
			{kitCredit ? (
				<Tag
					label="Kit Credit"
					kind="solid"
					size="sm"
					color="emerald"
				/>
			) : null}
			{consultantOnly ? (
				<Text
					tag="span"
					styledAs="xsr"
					className={styles["price__note"]}
				>
					Consultant pricing
				</Text>
			) : null}
		</div>
	);
}
